import { Controller } from "@hotwired/stimulus"

/**
 * Stimulus controller for the macro form's dynamic action builder.
 *
 * Each action row renders all possible value controls (one per action type)
 * inside `actionValueContainer` wrappers. Only the container matching the
 * selected action type is shown, and the `support_macro[action_values][]` name
 * is moved onto that container's control so exactly one value submits per row —
 * keeping action_types[] and action_values[] index-aligned server-side.
 *
 * Controls are CONEK Ui components (Tom Select / Ui::Input / Ui::Textarea), so
 * toggling happens on the wrapper (visibility) and on the inner control marked
 * with `data-macro-value-control` (name), never on the Tom Select wrapper.
 */
export default class extends Controller {
  static targets = [
    "actionsList",
    "actionRow",
    "actionTemplate",
    "emptyState",
    "actionType",
    "actionValueWrap",
    "actionValueContainer",
  ]

  static VALUE_NAME = "support_macro[action_values][]"

  connect() {
    this.actionIndex = this.actionRowTargets.length
    this.actionRowTargets.forEach((row) => this.syncRow(row))
    this.updateEmptyState()
  }

  addAction() {
    const template = this.actionTemplateTarget
    const html = template.innerHTML.replace(/__INDEX__/g, this.actionIndex++)
    this.actionsListTarget.insertAdjacentHTML("beforeend", html)
    const newRow = this.actionsListTarget.lastElementChild
    if (newRow) this.syncRow(newRow)
    this.updateEmptyState()
  }

  removeAction(event) {
    const row = event.target.closest(
      "[data-support--macro-form-target='actionRow']",
    )
    if (row) {
      row.remove()
      this.updateEmptyState()
    }
  }

  actionTypeChanged(event) {
    const row = event.target.closest(
      "[data-support--macro-form-target='actionRow']",
    )
    if (row) this.syncRow(row)
  }

  /**
   * Show the value container matching the row's selected action type and move
   * the value name onto its control; hide and de-name all others.
   */
  syncRow(row) {
    const typeSelect = row.querySelector(
      "[data-support--macro-form-target='actionType']",
    )
    const selectedType = typeSelect ? typeSelect.value : ""

    const containers = row.querySelectorAll(
      "[data-support--macro-form-target='actionValueContainer']",
    )

    containers.forEach((container) => {
      const matches = container.dataset.valueFor === selectedType
      container.classList.toggle("hidden", !matches)

      const control = container.querySelector("[data-macro-value-control]")
      if (!control) return

      if (matches) {
        control.setAttribute("name", this.constructor.VALUE_NAME)
      } else {
        control.removeAttribute("name")
      }
    })
  }

  updateEmptyState() {
    if (!this.hasEmptyStateTarget) return

    const hasActions =
      this.actionsListTarget.querySelectorAll(
        "[data-support--macro-form-target='actionRow']",
      ).length > 0
    this.emptyStateTarget.classList.toggle("hidden", hasActions)
  }
}
